import React from 'react';

import {Button, CheckIcon, Select} from '@kit';

import type {Ability} from '../../../../../generated/types';
import {useDice} from '../../../../../components/dice';
import {useCharacter} from '../../character-context';
import {ABILITIES} from '../../model/abilities';
import {AbilityGeneration, emptyRollGroup, RANDOM_ROLL_FORMULA, RandomRollGroup} from '../../model/ability-generation';
import type {CharacterState} from '../../model/character-state';
import {abilityLabel} from '../../model/labels';

import css from '../master.module.css';

type RandomGeneration = Extract<AbilityGeneration, {method: 'random'}>;

type Props = {
	generation: RandomGeneration;
};

const randomGeneration = (state: CharacterState): RandomGeneration =>
	state.abilityGeneration?.method === 'random'
		? state.abilityGeneration
		: {method: 'random', groups: [emptyRollGroup()]};

/**
 * Случайный набор: 4d6, три наибольших, шесть раз. Каждый бросок назначается характеристике;
 * попыток может быть несколько, в лист идёт та группа, которую сохранили.
 */
export const RandomScores: React.FC<Props> = ({generation}) => {
	const {update} = useCharacter();

	/** clearSavedGroup — группа, чьи назначения поменялись: её нужно сохранить заново */
	const updateGroups = (change: (groups: RandomRollGroup[]) => RandomRollGroup[], clearSavedGroup?: number) =>
		update((current) => {
			const previous = randomGeneration(current);
			return {
				abilityGeneration: {
					method: 'random',
					groups: change(previous.groups),
					savedGroup: previous.savedGroup === clearSavedGroup ? undefined : previous.savedGroup,
				},
			};
		});

	const saveGroup = (groupIndex: number) => {
		const group = generation.groups[groupIndex];
		update((current) => ({
			abilityGeneration: {...randomGeneration(current), savedGroup: groupIndex},
			baseAbilities: Object.fromEntries(
				ABILITIES.map((ability) => [ability, group.rolls[group.assigned[ability]!]!.total]),
			) as Record<Ability, number>,
		}));
	};

	return (
		<>
			{generation.groups.map((group, groupIndex) => (
				<RollGroup
					key={groupIndex}
					group={group}
					index={groupIndex}
					isSaved={generation.savedGroup === groupIndex}
					onChange={(changed) =>
						updateGroups(
							(groups) => groups.map((candidate, index) => (index === groupIndex ? changed : candidate)),
							groupIndex,
						)
					}
					onSave={() => saveGroup(groupIndex)}
				/>
			))}
			<Button onClick={() => updateGroups((groups) => [...groups, emptyRollGroup()])}>Ещё попытка</Button>
		</>
	);
};

type RollGroupProps = {
	group: RandomRollGroup;
	index: number;
	isSaved: boolean;
	/** назначения характеристик в группе поменялись */
	onChange(group: RandomRollGroup): void;
	onSave(): void;
};

const RollGroup: React.FC<RollGroupProps> = ({group, index, isSaved, onChange, onSave}) => {
	const {update} = useCharacter();
	const {roll} = useDice();
	const isComplete = ABILITIES.every((ability) => group.assigned[ability] !== undefined);

	const rollAt = (rollIndex: number) => {
		roll(RANDOM_ROLL_FORMULA, `Характеристика, попытка ${index + 1}`, (result) => {
			const dice = result.groups[0]?.values || [];
			update((current) => {
				const generation = randomGeneration(current);
				return {
					abilityGeneration: {
						...generation,
						groups: generation.groups.map((candidate, groupIndex) =>
							groupIndex === index
								? {
										...candidate,
										rolls: candidate.rolls.map((existing, position) =>
											position === rollIndex ? {dice, total: result.total} : existing,
										),
								  }
								: candidate,
						),
					},
				};
			});
		});
	};

	const assign = (rollIndex: number, ability: Ability | undefined) => {
		const assigned = Object.fromEntries(
			Object.entries(group.assigned).filter(([, position]) => position !== rollIndex),
		) as Partial<Record<Ability, number>>;
		if (ability) assigned[ability] = rollIndex;
		onChange({...group, assigned});
	};

	return (
		<section className={css.rollGroup}>
			<h3 className={css.startingTitle}>Попытка {index + 1}</h3>
			<div className={css.rollSlots}>
				{group.rolls.map((result, rollIndex) => {
					const ability = ABILITIES.find((candidate) => group.assigned[candidate] === rollIndex);
					const free = ABILITIES.filter(
						(candidate) => group.assigned[candidate] === undefined || candidate === ability,
					);
					return (
						<div key={rollIndex} className={css.rollSlot}>
							<span
								className={css.rollTotal}
								title={result ? `Кости: ${result.dice.join(', ')}` : undefined}
							>
								{result ? result.total : ' '}
							</span>
							{result ? (
								<>
									<Select
										items={free.map((candidate) => ({
											value: candidate,
											label: abilityLabel(candidate),
										}))}
										value={ability ?? null}
										placeholder="Характеристика"
										onChange={([value]) => assign(rollIndex, value as Ability | undefined)}
									/>
								</>
							) : (
								<Button onClick={() => rollAt(rollIndex)}>Бросить</Button>
							)}
						</div>
					);
				})}
			</div>
			<Button
				appearance={isSaved ? 'secondary' : 'primary'}
				icon={isSaved ? <CheckIcon /> : undefined}
				disabled={!isComplete}
				onClick={onSave}
			>
				{isSaved ? 'Сохранено в характеристики' : 'Сохранить характеристики'}
			</Button>
		</section>
	);
};
